import { BraintreeTokenizePayload } from '@paypal/react-paypal-js/dist/types/types/braintree/commonsTypes';
import { ExpressCheckoutElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import {
	ClickResolveDetails,
	StripeExpressCheckoutElementClickEvent,
	StripeExpressCheckoutElementConfirmEvent,
	StripeExpressCheckoutElementOptions,
	StripeExpressCheckoutElementReadyEvent,
	StripePaymentElementChangeEvent,
	StripePaymentElementOptions,
} from '@stripe/stripe-js';
import { useSearchParams } from 'next/navigation';
import { FormEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Notice, Text } from '@calm-web/design-system';

import messages from '@/components/purchase/CreditCardForm/messages';
import TrueMedWidget from '@/components/purchase/TrueMedWidget';
import { SubscribePurchaseError, subscribePurchaseErrors } from '@/components/purchase/utils/Purchase';
import usePurchaseBodyArgs from '@/components/Sisu/hooks/usePurchaseBodyArgs';
import { useAnalytics } from '@/hooks/analytics';
import { ProductInfo } from '@/hooks/analytics/types';
import { usePaymentFormValues } from '@/hooks/purchase/usePaymentFormValues';
import { usePaypalClientToken } from '@/hooks/purchase/usePaypalClientToken';
import { initLoggableSubscriptionDetails } from '@/hooks/purchase/usePurchaseSuccess';
import {
	useBrowserLanguageState,
	useDeviceState,
	usePricesState,
	usePurchaseParamsState,
	useRecaptchaVisibleState,
	useUserState,
} from '@/hooks/store';
import { setUser } from '@/store/user/actions';
import { Subscription } from '@/store/user/types';
import { requiresCustomPostal, usesZipCodeLabel } from '@/utils/payment/customPostalCode';
import {
	createUpsellPurchaseCompletedProps,
	UPSELL_PURCHASE_COMPLETED,
} from '@/utils/promoOffer/createUpsellPurchaseCompletedProps';
import { getPromoAttribution } from '@/utils/promoOffer/getPromoAttribution';
import { JSONObject } from '@/utils/types';
import { validate } from '@/utils/ui/validator';

import PaymentElementsFormSubmitButton from '../PaymentElementsFormSubmitButton';
import PaymentError from '../PaymentError';
import PaypalButton from '../PaypalButton';
import PurchaseTerms from '../PurchaseTerms';
import SecureTransaction from '../SecureTransaction';
import { TruemedCheckout } from '../TruemedCheckout';
import { hasTruemedIncompatiblePromo } from '../TruemedCheckout/hasTruemedIncompatiblePromo';
import { useTruemedEligibility } from '../TruemedCheckout/useTruemedEligibility';
import { useTruemedSelection } from '../TruemedCheckout/useTruemedSelection';
import _messages from './messages';
import {
	ExpressCheckoutWrapper,
	NameOnCardInput,
	NoticeWrapper,
	PostalCodeInput,
	TextWrapper,
	TruemedWrapper,
	Wrapper,
} from './styles';
import { OneTimePurchase, RequiresActionResponse } from './types';
import useSubmitPaymentRequest from './useSubmitPaymentRequest';
import { createSuccessEventProps } from './utils';

const EMPTY_PURCHASE_ARGS: Record<string, unknown> = {};

// Label for the custom postal input, matching Stripe's own copy: "ZIP code" for Puerto
// Rico, "Postal code" for CA and UK. (Clover gap-fill country set + predicate now live in
// the shared `@/utils/payment/customPostalCode` so PaymentElementsForm and Sisu/PaymentMethod
// can't drift.)
const postalLabelMessage = (country: string | null) =>
	usesZipCodeLabel(country) ? _messages.postalCodeLabelPR : _messages.postalCodeLabel;

// Keep the required-error wording in sync with the label (same `usesZipCodeLabel`
// predicate): "ZIP code" for PR, "postal code" for CA/UK — so error + label agree.
const postalRequiredMessage = (country: string | null) =>
	usesZipCodeLabel(country) ? _messages.postalCodeRequiredPR : _messages.postalCodeRequired;

export type PaymentElementsFormProps = {
	endpoint: string | ((args: Record<string, unknown>) => string);
	purchaseArgs?: Record<string, unknown>;
	successArgs?: Record<string, unknown>;
	ignoreSubscribeErrors?: boolean;
	subscribePurchaseErrors?: SubscribePurchaseError[];
	analyticsPrefix?: string;
	analyticsEventProps?: Record<string, string>;
	hidePriceAfterDisclaimer?: boolean;
	purchaseTerms?: string | ReactNode;
	ctaCopy?: string | ((plan: string | undefined) => string | undefined);
	productInfo?: ProductInfo;
	afterPurchaseSuccess?: () => void;
	onPurchaseSuccess?: (
		response: Record<string, unknown>,
		purchaseArgs: Record<string, unknown>,
	) => Promise<void>;
	hidePaypal?: boolean;
	hideApplePay?: boolean;
	hideTerms?: boolean;
	hasTrueMedSupport?: boolean;
	hideSubmitButton?: boolean;
	hideSecureTransaction?: boolean;
	onLoadingChange?: (isLoading: boolean) => void;
	onDisabledChange?: (isDisabled: boolean) => void;
	errorNoticeBackgroundColor?: string;
	pageName?: string;
	// Opt-in: only subscription checkout surfaces can sell a Truemed plan; gift, one-time,
	// partner and card-update surfaces must not show it.
	showTruemed?: boolean;
};

const PaymentElementsForm: React.FC<PaymentElementsFormProps> = ({
	endpoint,
	purchaseTerms,
	ctaCopy,
	onPurchaseSuccess: onPurchaseSuccessOverride,
	afterPurchaseSuccess,
	productInfo: productInfoOverride,
	analyticsPrefix = 'Subscribe : Purchase : Form',
	analyticsEventProps,
	purchaseArgs = EMPTY_PURCHASE_ARGS,
	successArgs = {},
	subscribePurchaseErrors: subscribePurchaseErrorsOverride,
	ignoreSubscribeErrors = false,
	hidePriceAfterDisclaimer = true,
	hidePaypal = false,
	hideApplePay = false,
	hideTerms = false,
	hasTrueMedSupport = false,
	hideSubmitButton = false,
	hideSecureTransaction = false,
	onLoadingChange,
	onDisabledChange,
	errorNoticeBackgroundColor,
	pageName,
	showTruemed = false,
}) => {
	const prices = usePricesState();
	const purchaseParams = usePurchaseParamsState();
	const { plan: formPlan, freeTrial, coupon, promotion } = usePurchaseBodyArgs({ purchaseArgs });
	// The row is submitted by this form's own button, so a surface that hides it can't offer Truemed.
	const truemed = useTruemedEligibility(formPlan, showTruemed && !hideSubmitButton);
	const truemedSelection = useTruemedSelection({
		plan: truemed.plan,
		freeTrial: !!freeTrial,
		analyticsPrefix,
	});
	const isTruemedActive = truemed.isEligible && truemedSelection.isOpen;
	const user = useUserState();
	const stripe = useStripe();
	const recaptchaVisible = useRecaptchaVisibleState();
	const elements = useElements();
	const searchParams = useSearchParams();
	const { formatMessage } = useIntl();
	const dispatch = useDispatch();
	const { paymentFormCurrency } = usePaymentFormValues();
	const browserLanguage = useBrowserLanguageState();
	const { logEvent, logEventAsync, logPurchase } = useAnalytics();
	const {
		clientToken: paypalClientToken,
		isSupported: isPaypalSupported,
		isLoading: isPaypalClientTokenFetching,
	} = usePaypalClientToken();
	const device = useDeviceState();

	const [selectedPaymentType, setSelectedPaymentType] = useState<string | null>(null);
	const [nameOnCard, setNameOnCard] = useState('');
	const [isPaymentElementsValid, setIsPaymentElementsValid] = useState(false);
	// Selected billing country drives whether we render our own postal input (the
	// Clover gap-fill for CA / UK / PR). Seeded from the same geo default the Payment
	// Element uses, then kept in sync from the element's change event.
	const [billingCountry, setBillingCountry] = useState<string | null>(
		prices.current.country ?? device.ip_country ?? null,
	);
	const [customPostal, setCustomPostal] = useState('');
	// The Payment Element's currently-selected method (card / us_bank_account / klarna /
	// …). Defaults to 'card' (the element's default tab); updated from its change event.
	const [paymentMethodType, setPaymentMethodType] = useState('card');
	// CA / UK / PR: the deferred Payment Element no longer auto-collects postal, so we
	// render our own input and inject it into billing_details at submit. All other
	// countries keep the built-in collection (US shows ZIP natively) — no custom field.
	// Only the embedded card method collects a billing postal — bank/Klarna/etc. have
	// their own flows, so the custom field is card-only.
	const showCustomPostal = requiresCustomPostal(billingCountry) && paymentMethodType === 'card';
	const postalIsValid = !showCustomPostal || customPostal.trim().length > 0;
	const [noSubscribeError] = useState(
		!ignoreSubscribeErrors && purchaseParams?.purchaseType?.type
			? (subscribePurchaseErrorsOverride ?? subscribePurchaseErrors).includes(
					purchaseParams.purchaseType.type as SubscribePurchaseError,
				)
			: false,
	);
	const [isLoading, setIsLoading] = useState(false);
	const [successfullySubmitted, setSuccessfullySubmitted] = useState(false);
	const [formMessage, setFormMessage] = useState<{ isSuccess: boolean; message: string } | null>(null);

	const isPaypalVisible = !hidePaypal && isPaypalSupported;

	const onPurchaseError = (
		errorMessageToDisplay: string,
		eventProps: JSONObject = {},
		errorCode?: string | JSONObject,
	) => {
		logEvent({
			eventName: `${analyticsPrefix} : Error`,
			eventProps: {
				currency: paymentFormCurrency,
				payment_type: selectedPaymentType,
				is_stripe_next: true,
				...(errorCode ? { error: errorCode } : {}),
				...eventProps,
				...analyticsEventProps,
			},
		});

		setFormMessage({
			isSuccess: false,
			message: errorMessageToDisplay,
		});

		onDisabledChange?.(false);
	};

	const onPurchaseSuccess = async (subscription: Subscription, eventProps: JSONObject = {}) => {
		const _user = {
			...user,
			subscription,
		};
		dispatch(setUser(_user));
		const transactionInfo = { id: _user?.id ?? null };
		const productInfo: ProductInfo = productInfoOverride ?? {
			name: 'web_subscription',
			sku: purchaseParams.plan,
			unit_price: purchaseParams?.purchaseType?.price ? purchaseParams?.purchaseType.price / 100.0 : 0,
			quantity: 1,
		};

		logPurchase({ productInfo, transactionInfo });

		const subscriptionDetails = initLoggableSubscriptionDetails(_user?.subscription);
		const successEventProps = createSuccessEventProps({
			productInfo,
			prices,
			purchaseParams,
			selectedPaymentType,
			paymentFormCurrency,
			browserLanguage,
			userEmail: _user?.email,
			successArgs: {
				is_stripe_next: true,
				source: searchParams?.get('source') ?? '',
				...getPromoAttribution(searchParams),
				...subscriptionDetails,
				...eventProps,
				...successArgs,
			},
		});

		setFormMessage({
			isSuccess: true,
			message: formatMessage(_messages.success),
		});

		await logEventAsync({
			eventName: `${analyticsPrefix} : Success`,
			eventProps: { ...successEventProps, ...analyticsEventProps },
		});

		const upsellPurchaseProps = createUpsellPurchaseCompletedProps({
			searchParams,
			subscription,
			plan: productInfo.sku,
			browserLanguage,
		});
		// Not awaited, so a slow analytics call cannot hold up the post-purchase redirect.
		if (upsellPurchaseProps && productInfo.sku !== 'gift_card') {
			logEvent({ eventName: UPSELL_PURCHASE_COMPLETED, eventProps: upsellPurchaseProps });
		}

		afterPurchaseSuccess?.();
	};

	const oneTimePurchaseSuccess = async (transaction: OneTimePurchase, eventProps: JSONObject = {}) => {
		const transactionInfo = { id: user?.id ?? '' };
		const productInfo: ProductInfo = productInfoOverride ?? {
			name: 'one_time_purchase',
			sku: purchaseParams.plan,
			unit_price: purchaseParams?.purchaseType?.price ? purchaseParams.purchaseType.price / 100.0 : 0,
			quantity: 1,
		};

		logPurchase({ productInfo, transactionInfo });

		const successEventProps = createSuccessEventProps({
			productInfo,
			prices,
			purchaseParams,
			selectedPaymentType,
			paymentFormCurrency,
			browserLanguage,
			userEmail: user?.email,
			successArgs: {
				is_stripe_next: true,
				source: searchParams?.get('source') ?? '',
				...transaction,
				...eventProps,
				...successArgs,
			},
		});

		setFormMessage({
			isSuccess: true,
			message: formatMessage(_messages.success),
		});

		await logEventAsync({
			eventName: `${analyticsPrefix} : Success`,
			eventProps: { ...successEventProps, ...analyticsEventProps },
		});

		afterPurchaseSuccess?.();
	};

	const submitPaymentRequest = useSubmitPaymentRequest({
		analyticsEventProps,
		analyticsPrefix,
		endpoint,
		hasTrueMedSupport,
		oneTimePurchaseSuccess,
		onPurchaseError,
		onPurchaseSuccess,
		onPurchaseSuccessOverride,
		onRequiresAction,
		pageName,
		purchaseArgs,
		selectedPaymentType,
		setIsLoading,
		setSuccessfullySubmitted,
	});

	async function onRequiresAction({ clientSecret, customerId, subscriptionId }: RequiresActionResponse) {
		if (!stripe) return;

		logEvent({
			eventName: `${analyticsPrefix} : Requires Action`,
			eventProps: {
				payment_type: selectedPaymentType,
				is_stripe_next: true,
				...analyticsEventProps,
			},
		});

		const { paymentIntent, setupIntent, error } = await stripe.handleNextAction({
			clientSecret,
		});

		if (error) {
			if (error.message) {
				setFormMessage({
					isSuccess: false,
					message: error.message,
				});
			}
			setIsLoading(false);
			return;
		}

		// A payment intent is created for purchases that require immediate payment
		// A setup intent is created for purchases that instead kick off a free trial
		const stripeIntent = paymentIntent ?? setupIntent;

		switch (stripeIntent?.status) {
			case 'succeeded':
				setFormMessage(null);
				setIsLoading(true);
				await submitPaymentRequest({
					paymentMethod: 'stripe',
					stripeCustomerId: customerId,
					stripeIntentId: stripeIntent.id,
					stripePaymentMethodId: stripeIntent.payment_method,
					stripeSubscriptionId: subscriptionId,
				});
				break;
			case 'processing':
				onPurchaseError(formatMessage(_messages.processing), {}, paymentIntent?.status);
				break;
			case 'requires_payment_method':
				onPurchaseError(formatMessage(_messages.unsuccessful), {}, paymentIntent?.status);
				break;
			default:
				onPurchaseError(formatMessage(_messages.fallback));
				break;
		}
	}

	const onPaypalSubmit = async (paypalToken: BraintreeTokenizePayload) => {
		setFormMessage(null);
		setIsLoading(true);

		// Track payment button click
		logEvent({
			eventName: 'Subscription : Upsell : Clicked',
			eventProps: {
				payment_type: 'paypal',
				is_stripe_next: true,
				...analyticsEventProps,
			},
		});

		await submitPaymentRequest({ paypalToken, paymentMethod: 'paypal' }, { payment_type: 'paypal' });
	};

	const onStripeSubmit = async (e: FormEvent | StripeExpressCheckoutElementConfirmEvent) => {
		// A form submit (card path) has preventDefault; the express/wallet confirm event
		// does not. Only the card path collects our custom postal — wallets carry address.
		const isCardSubmit = 'preventDefault' in e;
		if ('preventDefault' in e) {
			e.preventDefault();
		}
		if (isCardSubmit && isTruemedActive) {
			await truemedSelection.start();
			return;
		}
		if (!stripe || !elements || isLoading) return;

		// Clover gap-fill (CA / UK / PR): require + inject the custom postal into
		// billing_details on the card path so it reaches Stripe via createPaymentMethod.
		// `showCustomPostal` already scopes this to the card method.
		const collectCustomPostal = isCardSubmit && showCustomPostal;
		if (collectCustomPostal && customPostal.trim().length === 0) {
			setFormMessage({ isSuccess: false, message: formatMessage(postalRequiredMessage(billingCountry)) });
			return;
		}
		const customBillingAddress = collectCustomPostal ? { address: { postal_code: customPostal.trim() } } : {};

		setFormMessage(null);
		setIsLoading(true);

		// Track the CARD payment-button click. Gate on `isCardSubmit`: the wallet/express path
		// reaches here via `expressCheckoutOnConfirm → onStripeSubmit`, but `expressCheckoutOnClick`
		// already fired the correctly wallet-typed `Upsell : Clicked` — firing this hardcoded
		// `credit_card` one again would double-count (it OVERSTATES credit-card clicks by the number
		// of completed wallet purchases, skewing the payment_type mix toward card). Same
		// `isCardSubmit` guard as the postal block above.
		if (isCardSubmit) {
			logEvent({
				eventName: 'Subscription : Upsell : Clicked',
				eventProps: {
					payment_type: 'credit_card',
					is_stripe_next: true,
					...analyticsEventProps,
				},
			});
		}

		// Validate the PaymentElement before tokenizing. An incomplete/invalid element
		// returns a submit error here; surface it and stop rather than proceeding to
		// createPaymentMethod() (which would yield a confusing/secondary error). Mirrors
		// the handling in Sisu/hooks/useSubmitStripePayment.ts.
		const { error: submitError } = await elements.submit();
		if (submitError) {
			if (submitError.message) {
				setFormMessage({ isSuccess: false, message: submitError.message });
			}
			setIsLoading(false);
			return;
		}

		const { error, paymentMethod } = await stripe.createPaymentMethod({
			elements,
			params: {
				billing_details: {
					name: nameOnCard,
					email: user?.email,
					...customBillingAddress,
				},
			},
		});

		if (error) {
			if (error.message) {
				setFormMessage({
					isSuccess: false,
					message: error.message,
				});
			}
			setIsLoading(false);
			return;
		}

		// Synchronous status-quo flow: POST the PaymentMethod id; reactive SCA (3DS) is
		// driven by the response's `onRequiresAction` inside submitPaymentRequest.
		await submitPaymentRequest({
			stripePaymentMethodId: paymentMethod.id,
			stripeAddress: paymentMethod.billing_details.address,
			paymentMethod: 'stripe',
		});
	};

	const onPaymentElementChange = (e: StripePaymentElementChangeEvent) => {
		setIsPaymentElementsValid(e.complete);
		// Track the selected method (card / bank / klarna) so the custom postal — a
		// card-billing concern — only shows for card. Default to 'card' when absent.
		setPaymentMethodType(e.value?.type ?? 'card');
		// Track the billing country so the custom postal field appears/disappears as the
		// buyer changes country in the element (the geo seed covers the initial paint).
		// Guard `e.value` too: sibling consumers of this shared form fire change-event
		// mocks without a `value`, and a partial event shouldn't crash the handler.
		const country = e.value?.billingDetails?.address?.country;
		if (country) {
			setBillingCountry(country);
		}
	};

	useEffect(() => {
		if (selectedPaymentType) {
			logEvent({
				eventName: 'Subscribe : Payment Type : Selected',
				eventProps: {
					payment_type: selectedPaymentType,
					is_stripe_next: true,
				},
			});
		}
	}, [selectedPaymentType, logEvent]);

	const nameIsValid = selectedPaymentType === 'credit_card' ? validate('name', nameOnCard) : true;
	const isDisabled = isTruemedActive
		? recaptchaVisible || isLoading || !truemedSelection.isZipValid || truemedSelection.isStarting
		: !stripe || recaptchaVisible || !isPaymentElementsValid || !nameIsValid || !postalIsValid || isLoading;
	// With the `tabs` layout the Payment Element can't collapse, so focusing any card field is the
	// signal that the buyer has moved off Truemed.
	const onCardFieldFocus = () => {
		setSelectedPaymentType('credit_card');
		truemedSelection.close();
	};

	// Call loading change callback
	useEffect(() => {
		onLoadingChange?.(isLoading);
	}, [isLoading, onLoadingChange, successfullySubmitted]);

	// Call disabled change callback
	useEffect(() => {
		onDisabledChange?.(isDisabled);
	}, [isDisabled, onDisabledChange]);

	const userEmail = user?.email;
	const userName = user?.name;
	const priceCountry = prices.current.country;
	const deviceIpCountry = device.ip_country;
	const paymentElementOptions: StripePaymentElementOptions = useMemo(
		() => ({
			layout: 'tabs',
			defaultValues: {
				billingDetails: {
					email: userEmail,
					name: userName,
					address: {
						// Source the default country from the live selection (billingCountry)
						// so it survives Element re-init — Stripe applies defaultValues only at
						// create/re-init, so seeding from geo alone reverts a re-created Element
						// (e.g. the dev overlay's "Customer country") back to the geo country.
						// Falls back to the geo seed for the initial paint.
						country: billingCountry ?? priceCountry ?? deviceIpCountry ?? undefined,
					},
				},
			},
		}),
		[userEmail, userName, billingCountry, priceCountry, deviceIpCountry],
	);

	const expressCheckoutElementOptions: StripeExpressCheckoutElementOptions = {
		buttonHeight: 55,
		paymentMethods: {
			applePay: hideApplePay ? 'never' : 'auto',
		},
		billingAddressRequired: true,
	};

	const expressCheckoutOnConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
		await onStripeSubmit(event);
	};

	const expressCheckoutOnCancel = () => {
		logEvent({
			eventName: `${analyticsPrefix} : Cancelled`,
			eventProps: {
				payment_type: selectedPaymentType,
				...analyticsEventProps,
			},
		});
	};

	const expressCheckoutOnClick = (event: StripeExpressCheckoutElementClickEvent) => {
		setSelectedPaymentType(event.expressPaymentType);

		// Track payment button click
		logEvent({
			eventName: 'Subscription : Upsell : Clicked',
			eventProps: {
				payment_type: event.expressPaymentType,
				is_stripe_next: true,
				...analyticsEventProps,
			},
		});

		const isFreeTrial = purchaseParams?.purchaseType?.type === 'freetrial';
		const deferredPaymentDate = new Date();
		deferredPaymentDate.setDate(
			deferredPaymentDate.getDate() + (purchaseParams?.purchaseType?.duration ?? 14),
		);
		const freeTrialResolveArgs: ClickResolveDetails = isFreeTrial
			? {
					applePay: {
						deferredPaymentRequest: {
							managementURL: process.env.NEXT_PUBLIC_WWW_URL as string,
							paymentDescription: formatMessage(_messages.applePayPaymentDescription),
							deferredBilling: {
								deferredPaymentDate,
								label: formatMessage(_messages.applePayPaymentDescription),
								amount: 0,
							},
						},
					},
				}
			: {};

		event.resolve({
			...freeTrialResolveArgs,
		});
	};

	const [showOr, setShowOr] = useState(false);

	const expressCheckoutOnReady = (event: StripeExpressCheckoutElementReadyEvent) => {
		setShowOr(!!event.availablePaymentMethods);
	};

	// Track when payment form fails to load due to invalid purchase type
	useEffect(() => {
		if (noSubscribeError) {
			logEvent({
				eventName: 'Subscription : Upsell : Failed To Load',
				eventProps: {
					error_type: 'invalid_purchase_type',
					...(purchaseParams?.purchaseType?.type && { purchase_type: purchaseParams.purchaseType.type }),
					...analyticsEventProps,
				},
			});
		}
	}, [noSubscribeError, purchaseParams?.purchaseType?.type, analyticsEventProps, logEvent]);

	if (noSubscribeError) {
		return <PaymentError errorType={purchaseParams?.purchaseType?.type as SubscribePurchaseError} />;
	}

	return (
		<Wrapper onSubmit={onStripeSubmit} id="purchase-form">
			<ExpressCheckoutWrapper>
				<ExpressCheckoutElement
					options={expressCheckoutElementOptions}
					onConfirm={expressCheckoutOnConfirm}
					onClick={expressCheckoutOnClick}
					onCancel={expressCheckoutOnCancel}
					onReady={expressCheckoutOnReady}
				/>
			</ExpressCheckoutWrapper>
			{isPaypalVisible && (
				<PaypalButton
					setSelectedPaymentType={setSelectedPaymentType}
					onSubmit={onPaypalSubmit}
					onError={onPurchaseError}
					clientToken={paypalClientToken}
					isLoading={isPaypalClientTokenFetching ?? (selectedPaymentType === 'paypal' && isLoading)}
					isDisabled={recaptchaVisible || isLoading}
					analyticsPrefix={analyticsPrefix}
					analyticsEventProps={analyticsEventProps}
				/>
			)}
			{showOr && (
				<TextWrapper>
					<Text color="blackAlpha60">{formatMessage(_messages.or)}</Text>
				</TextWrapper>
			)}
			<TextWrapper>
				<NameOnCardInput
					name="name"
					label={formatMessage(messages.firstName)}
					value={nameOnCard}
					isValid={nameIsValid}
					onChange={e => setNameOnCard(e.target.value)}
					error={formatMessage(messages.firstNameError)}
					labelFocusScaleTransform={0.85}
					onFocus={onCardFieldFocus}
					autoComplete="cc-name"
				/>
				<PaymentElement
					onChange={onPaymentElementChange}
					options={paymentElementOptions}
					onFocus={onCardFieldFocus}
				/>
				{/* Clover gap-fill (CA / UK / PR): the Payment Element drops postal for these
				    regions, so collect it ourselves and inject it into billing_details at
				    submit. Card-only (bank/Klarna have their own flows); for US/other
				    countries the built-in postal is used and this never renders. */}
				{showCustomPostal && (
					<PostalCodeInput
						label={formatMessage(postalLabelMessage(billingCountry))}
						name="postalCode"
						type="text"
						value={customPostal}
						onChange={e => setCustomPostal(e.currentTarget.value)}
						onFocus={onCardFieldFocus}
						autoComplete="postal-code"
					/>
				)}
			</TextWrapper>
			{truemed.isEligible && truemed.plan && (
				<TruemedWrapper>
					<TruemedCheckout
						selection={truemedSelection}
						hasPromo={hasTruemedIncompatiblePromo({ coupon, promotion })}
					/>
				</TruemedWrapper>
			)}
			{!hideTerms && (
				<PurchaseTerms hidePriceAfterDisclaimer={hidePriceAfterDisclaimer} purchaseTerms={purchaseTerms} />
			)}
			{!hideSubmitButton && (
				<PaymentElementsFormSubmitButton
					isDisabled={isDisabled}
					isLoading={isLoading || (isTruemedActive && truemedSelection.isStarting)}
					ctaCopy={ctaCopy}
				/>
			)}
			{hasTrueMedSupport && <TrueMedWidget />}
			{!hideSecureTransaction && <SecureTransaction />}
			{formMessage?.message && (
				<NoticeWrapper>
					<Notice
						isSuccess={formMessage.isSuccess}
						onClick={() => setFormMessage(null)}
						describedBy="purchase-form"
						errorBackgroundColor={errorNoticeBackgroundColor}
					>
						{formMessage.message}
					</Notice>
				</NoticeWrapper>
			)}
		</Wrapper>
	);
};

export default PaymentElementsForm;
